import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc } from "@editor/utils/trpc";

import { skipToken } from "@tanstack/react-query";

import { useSaveHtmlContent } from "./page-cache";
import { useWebContainerHook } from "./useWebContainerHook";

type WebContainerContextType = ReturnType<typeof useWebContainerHook> & {
  refetchFiles: () => Promise<void>;
};

const WebContainerContext = React.createContext<
  WebContainerContextType | undefined
>(undefined);

const WebContainerProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const projectId = useCurrentProjectId();

  useSaveHtmlContent({ projectId });

  const { data: fileTree, refetch } = trpc.webContainer.getFileTree.useQuery(
    projectId && isFeatureEnabled("reaplo") ? { projectId } : skipToken,
  );

  const result = useWebContainerHook({ fileTree });

  const refetchFiles = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <WebContainerContext.Provider
      value={{
        ...result,
        refetchFiles,
      }}
    >
      {children}
    </WebContainerContext.Provider>
  );
};

function useWebContainer() {
  const context = React.useContext(WebContainerContext);
  if (context === undefined) {
    throw new Error(
      "useWebContainer must be used within a WebContainerProvider",
    );
  }
  return context;
}

export { WebContainerProvider, useWebContainer };
